import { useCallback, useEffect, useRef, type MutableRefObject } from "react";
import type { Budget } from "../../types/budget/budget";
import { useEstudoAutosaveContext } from "./estudo-autosave-context";
import type { DraftState } from "./domain/draft";
import { draftToBudget, stampSavedServiceIds } from "./domain/persist";

/** Mesmo intervalo de `BUDGET_AUTOSAVE_INTERVAL_MS` no orçamento. */
export const ESTUDO_AUTOSAVE_INTERVAL_MS = 60000;

/** Mesmo debounce de `BUDGET_SERVICE_WRITES_DEBOUNCE_MS` no orçamento. */
export const ESTUDO_AUTOSAVE_DEBOUNCE_MS = 750;

/** A 1ª falha só dispensa o loading; o toast de erro sai a partir da 2ª. */
export const ESTUDO_AUTOSAVE_ERROR_TOAST_THRESHOLD = 2;

export type EstudoAutosaveSource =
  | "debounce"
  | "interval"
  | "navigation"
  | "manual";

type UpdateService = (
  serviceId: string,
  serviceData: Budget,
  action?: string,
  version?: number,
  options?: { silent?: boolean }
) => Promise<{
  _id?: string;
  budgetServices?: Array<{ _id?: string; sortOrder?: number }>;
} | null>;

type UseEstudoAutosaveArgs = {
  draftRef: MutableRefObject<DraftState>;
  setDraft: (next: DraftState) => void;
  activeService: Budget | null | undefined;
  serviceRef: MutableRefObject<Budget | null | undefined>;
  dirtyRef: MutableRefObject<boolean>;
  updateService: UpdateService;
  dispatchSaved: (saved: Budget) => void;
  enabled: boolean;
};

export function useEstudoAutosave({
  draftRef,
  setDraft,
  activeService,
  serviceRef,
  dirtyRef,
  updateService,
  dispatchSaved,
  enabled,
}: UseEstudoAutosaveArgs) {
  const { register, publish, notifySuccess, notifyErrorToast } =
    useEstudoAutosaveContext();

  const revisionRef = useRef(0);
  const savedRevisionRef = useRef(0);
  const runningRef = useRef(false);
  const queuedRef = useRef(false);
  const manualQueuedRef = useRef(false);
  const navigationQueuedRef = useRef(false);
  const failuresRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chainRef = useRef<Promise<boolean> | null>(null);
  const enabledRef = useRef(enabled);
  const updateServiceRef = useRef(updateService);
  const dispatchSavedRef = useRef(dispatchSaved);
  const runFlushRef = useRef<
    (source: EstudoAutosaveSource) => Promise<boolean>
  >(async () => false);

  enabledRef.current = enabled;
  updateServiceRef.current = updateService;
  dispatchSavedRef.current = dispatchSaved;

  if (!runningRef.current) {
    serviceRef.current = activeService;
  }

  const hasPending = useCallback(
    () => revisionRef.current !== savedRevisionRef.current,
    []
  );

  const clearDebounce = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, []);

  const flush = useCallback(
    (source: EstudoAutosaveSource) => runFlushRef.current(source),
    []
  );

  const scheduleDebounce = useCallback(() => {
    clearDebounce();
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      void flush("debounce");
    }, ESTUDO_AUTOSAVE_DEBOUNCE_MS);
  }, [clearDebounce, flush]);

  const markEdited = useCallback(() => {
    revisionRef.current += 1;
    dirtyRef.current = true;
    publish({
      hasPendingChanges: true,
      status: runningRef.current ? undefined : "unsaved",
    });
    if (runningRef.current) {
      queuedRef.current = true;
      clearDebounce();
      return;
    }
    scheduleDebounce();
  }, [clearDebounce, dirtyRef, publish, scheduleDebounce]);

  useEffect(() => {
    runFlushRef.current = async (source: EstudoAutosaveSource) => {
      if (source !== "interval") clearDebounce();

      if (runningRef.current) {
        queuedRef.current = true;
        if (source === "manual") manualQueuedRef.current = true;
        if (source === "navigation") navigationQueuedRef.current = true;
        return chainRef.current ?? Promise.resolve(false);
      }

      const chain = (async () => {
        let current = source;
        let ok = true;

        while (true) {
          queuedRef.current = false;
          const pending = revisionRef.current !== savedRevisionRef.current;
          if (!pending && current !== "manual") {
            ok = true;
            break;
          }

          const service = serviceRef.current;
          if (!enabledRef.current || !service?._id) {
            ok = false;
            break;
          }

          runningRef.current = true;
          const sentRevision = revisionRef.current;
          const snapshot = draftRef.current;
          const sentIds = snapshot.items.map((item) => item.id);
          const automatic = current !== "manual";
          if (automatic) {
            publish({ status: "saving", hasPendingChanges: true });
          }

          const saved = await updateServiceRef.current(
            service._id,
            draftToBudget(service, snapshot),
            undefined,
            undefined,
            { silent: automatic }
          );
          runningRef.current = false;

          const persisted = Boolean(
            saved && (saved._id || saved.budgetServices)
          );
          if (!persisted) {
            ok = false;
            if (automatic) {
              failuresRef.current += 1;
              publish({
                status: "error",
                hasPendingChanges:
                  revisionRef.current !== savedRevisionRef.current,
              });
              if (
                failuresRef.current >= ESTUDO_AUTOSAVE_ERROR_TOAST_THRESHOLD
              ) {
                notifyErrorToast();
              }
              if (queuedRef.current) {
                queuedRef.current = false;
                manualQueuedRef.current = false;
                navigationQueuedRef.current = false;
                scheduleDebounce();
              }
            }
            break;
          }

          const stamped = stampSavedServiceIds(
            draftRef.current,
            sentIds,
            saved!.budgetServices || []
          );
          setDraft(stamped);
          serviceRef.current = {
            ...service,
            ...saved,
          } as Budget;
          dispatchSavedRef.current(serviceRef.current);

          const caughtUp = revisionRef.current === sentRevision;
          const manualWaiting = manualQueuedRef.current;
          if (caughtUp) {
            savedRevisionRef.current = sentRevision;
            dirtyRef.current = false;
            if (automatic) {
              failuresRef.current = 0;
              publish({ status: "saved", hasPendingChanges: false });
              if (!manualWaiting) notifySuccess();
            } else {
              publish({ hasPendingChanges: false, status: "saved" });
            }
          } else if (automatic) {
            dirtyRef.current = true;
            publish({ status: "unsaved", hasPendingChanges: true });
          }

          const followUp =
            queuedRef.current ||
            (!caughtUp && (current === "navigation" || current === "manual"));
          if (!followUp) {
            ok = true;
            manualQueuedRef.current = false;
            navigationQueuedRef.current = false;
            break;
          }

          const nextSource: EstudoAutosaveSource = manualQueuedRef.current
            ? "manual"
            : navigationQueuedRef.current || current === "navigation"
            ? "navigation"
            : "debounce";
          manualQueuedRef.current = false;
          navigationQueuedRef.current = false;
          queuedRef.current = false;
          current = nextSource;
        }

        return ok;
      })().finally(() => {
        chainRef.current = null;
        runningRef.current = false;
      });

      chainRef.current = chain;
      return chain;
    };
  }, [
    clearDebounce,
    dirtyRef,
    draftRef,
    notifyErrorToast,
    notifySuccess,
    publish,
    scheduleDebounce,
    serviceRef,
    setDraft,
  ]);

  useEffect(() => {
    register({
      hasPending,
      flush: () => flush("navigation"),
    });
    return () => {
      clearDebounce();
      register(null);
    };
  }, [clearDebounce, flush, hasPending, register]);

  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(() => {
      if (!enabledRef.current || runningRef.current) return;
      if (revisionRef.current === savedRevisionRef.current) return;
      void flush("interval");
    }, ESTUDO_AUTOSAVE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [enabled, flush]);

  return { markEdited, flush, hasPending };
}
